import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { LIVE, LiveConfig, SeatLive, LiveCandidate, LiveUpdate } from "@/data/live";

/**
 * Hook that merges database-driven live results with the static candidate
 * lineup from live.ts. Polls the API every 15 seconds so the public page
 * updates automatically when the admin panel saves new data.
 *
 * Returns a LiveConfig-shaped object that can be used as a drop-in
 * replacement for the static LIVE constant.
 */
export function useLiveData(): LiveConfig {
  const POLL_MS = 15_000;

  const { data: dbResults } = trpc.election.getResults.useQuery(undefined, {
    refetchInterval: POLL_MS,
    staleTime: 5_000,
  });

  const { data: dbVotes } = trpc.election.getVotes.useQuery(undefined, {
    refetchInterval: POLL_MS,
    staleTime: 5_000,
  });

  const { data: dbFeed } = trpc.election.getFeed.useQuery(undefined, {
    refetchInterval: POLL_MS,
    staleTime: 5_000,
  });

  const { data: dbConfig } = trpc.election.getConfig.useQuery(undefined, {
    refetchInterval: POLL_MS,
    staleTime: 5_000,
  });

  return useMemo(() => {
    // Build seatStatus from DB results
    const seatStatus: Record<string, SeatLive> = {};
    if (dbResults) {
      for (const r of dbResults) {
        seatStatus[r.seatCode] = {
          status: r.status as SeatLive["status"],
          party: r.party,
          candidate: r.candidate ?? undefined,
          sub: r.sub ?? undefined,
          majority: r.majority ?? undefined,
          registered: r.registered ?? undefined,
          valid: r.valid ?? undefined,
          turnoutPct: r.turnoutPct ? parseFloat(r.turnoutPct) : undefined,
        };
      }
    }

    // Build candidatesByContest: start with static data, overlay DB votes
    const candidatesByContest: Record<string, LiveCandidate[]> = {};
    const votesBySeat: Record<string, Record<string, number>> = {};
    if (dbVotes) {
      for (const v of dbVotes) {
        if (!votesBySeat[v.seatCode]) votesBySeat[v.seatCode] = {};
        if (v.votes != null) votesBySeat[v.seatCode][v.candidateName] = v.votes;
      }
    }
    for (const [code, candidates] of Object.entries(LIVE.candidatesByContest)) {
      const seatVotes = votesBySeat[code];
      candidatesByContest[code] = candidates.map((c) => ({
        ...c,
        votes: seatVotes?.[c.name] ?? c.votes,
      }));
    }

    // Build feed: DB feed takes priority, fall back to static
    const feed: LiveUpdate[] =
      dbFeed && dbFeed.length > 0
        ? dbFeed.map((f) => ({
            date: f.date ?? undefined,
            time: f.time,
            text: f.text,
            linkTitle: f.linkTitle ?? undefined,
          }))
        : LIVE.feed;

    // Config
    const isLive = dbConfig ? dbConfig.isLive === 1 : LIVE.isLive;
    const lastUpdated = dbConfig?.lastUpdated ?? LIVE.lastUpdated;

    return {
      isLive,
      electionName: LIVE.electionName,
      level: LIVE.level,
      totalSeats: LIVE.totalSeats,
      majorityNeeded: LIVE.majorityNeeded,
      lastUpdated,
      sourceNote: LIVE.sourceNote,
      feed,
      seatStatus,
      candidatesByContest,
    };
  }, [dbResults, dbVotes, dbFeed, dbConfig]);
}
